import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useLayoutEffect } from "react";
import { useHistory } from "react-router-dom";
import { useFindingBucket } from "contexts/FindingBucketContext";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import FindingDetailsProvider, { useFindingDetails } from "../FindingDetailsProvider";
import FindingDetailsTitle from "../FindingDetailsTitle";
import OverviewTabContent from "../OverviewTabContent";
import { FindingDetailsComments } from "../FindingDetailsComments";
import { FindingProposals } from "views/Findings/FindingDetailsModalV2";
import { FindingDetailsTag } from "../FindingDetailsTag";
import TrainingAIDetails from "../TrainingAIDetails";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import useAPIResponse from "hooks/useAPIResponse";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";
import ACTabs from "ac-components/ac-tabs/ACTabs";
import ACPageContainer from "ac-components/ACPageContainer/ACPageContainer";
import ACTabContent from "ac-components/ac-tab-content/ACTabContent";
import RemediateByTool from "../RemediateByTool";
import CorrelationsTab from "../correlation/CorrelationsTab";
import ComplianceTab from "../ComplianceTab";
import PinnableFields from "../PinnableFields/PinnableFields";
import ACBadge from "ac-components/ac-badge/ACBadge";
import { getFindingDetailsLastTab, getLocalStorage, setFindingDetailsLastTab, setLocalStorage } from "utils/localStorageUtils";
import ACPanelResize from "ac-components/ac-panel-resize/ACPanelResize";
import useDimensions from "hooks/useDimensions";
import { useFeatureFlag } from "contexts/FeatureFlagContext";


const FindingDetailsModalV3 = forwardRef(function FindingDetailsModalV3(
  { findingId: id, findingNavigatorProps, isMobbSecurityConfigured = false },
  ref,
) {
  const thisRef = useRef();
  const history = useHistory();
  const bucket = useFindingBucket();
  const { updateGlobalFilters } = useGlobalFilterContext();

  const [aiRemediationTab, loading] = useAPIResponse(
    () => GlobalSettingAPI.getRemediationGuidance(),
    [],
  );

  useImperativeHandle(ref, () => ({ ...thisRef.current }), []);

  return (
    <FindingDetailsProvider
      id={id}
      bucket={bucket}
      history={history}
      updateGlobalFilters={updateGlobalFilters}
      mainModalRef={thisRef}
      findingNavigatorProps={findingNavigatorProps}
    >
      <div className="finding-detail-modal-wrapper" tabIndex={0}>
        <ACModalV2.Title>
          <FindingDetailsTitle mainModalRef={thisRef} />
        </ACModalV2.Title>
        <div className="finding-modal-v3">
          <div className="finding-modal-v3-body">
            <FindingDetailsModalContent
              loading={loading}
              aiRemediationTab={aiRemediationTab}
              isMobbSecurityConfigured={isMobbSecurityConfigured}
            />
          </div>
        </div>
      </div>
    </FindingDetailsProvider>
  );
});
const WIDTH_SETTING_KEY = "finding-details-left-panel-width";
const FindingDetailsModalContent = ({
  loading,
  aiRemediationTab,
  isMobbSecurityConfigured,
}) => {
  const [activeTab, setActiveTab] = useState(() => getFindingDetailsLastTab());
  const { findingCorrelations } = useFindingDetails();
  const featureFlags = useFeatureFlag();
  const [ref, dimensions] = useDimensions({ continuousMeasure: true });
  const containerWidth = dimensions.width;
  const maxWidth = containerWidth * 0.5,
    minWidth = 335;
  const [width, setWidth] = useState(0);
  const leftPanelWidth = `calc(100% - ${width}px)`;

  useEffect(() => {
    setWidth(Math.min(getLocalStorage(WIDTH_SETTING_KEY, minWidth), maxWidth));
  }, [containerWidth, minWidth, maxWidth]);

  useEffect(() => {
    setFindingDetailsLastTab(activeTab);
  }, [activeTab]);

  return (
    <>
      <ACPageContainer className="m-0" ref={ref}>
        <FindingProposals />
        <ACPageContainer.Section className="finding-modal-v3-tabs-section">
          <ACTabs
            value={activeTab}
            onChange={setActiveTab}
            responsive
            items={[
              { label: "Overview", value: "general" },
              ...(isMobbSecurityConfigured ? [{ label: "Remediate", value: "remediateByTool" }] : []),
              { label: "Risk & Tags", value: "tags" },
              {
                label: "Training",
                value: "training",
              },
              {
                label: (
                  <span className="d-flex align-items-center gap-8">
                    Correlations
                    <ACBadge type="beta" content={findingCorrelations.length} />
                  </span>
                ),
                value: "correlatedFindings",
              },
              ...(featureFlags.CRA ? [{
                label: "Compliance",
                value: "compliance",
              }] : []),
            ]}
          />
        </ACPageContainer.Section>
        <div className="finding-detail-modal-v3-section-container">
          <div className="left-section" style={{ width: leftPanelWidth }}>
            <ACPageContainer.Section equalPadding>
              <ACTabContent
                value={activeTab}
                items={[
                  {
                    value: "general",
                    content: <OverviewTabContent aiRemediationTab={aiRemediationTab} loading={loading} />,
                  },
                  ...(isMobbSecurityConfigured ? [{
                    value: "remediateByTool",
                    content: <RemediateByTool />,
                  }] : []),
                  {
                    value: "tags",
                    content: <FindingDetailsTag />,
                  },
                  {
                    value: "training",
                    content: <TrainingAIDetails />,
                  },
                  {
                    value: "correlatedFindings",
                    content: <CorrelationsTab />,
                  },
                  ...(featureFlags.CRA ? [{
                    value: "compliance",
                    content: <ComplianceTab />,
                  }] : []),
                ]}
              />
            </ACPageContainer.Section>
            <ACPageContainer.Section equalPadding borderBottom={false}>
              <FindingDetailsComments />
            </ACPageContainer.Section>
          </div>
            <ACPanelResize
              resizeFrom="left"
              onChange={(w) => {
                const _w = Math.max(Math.min(w, maxWidth), minWidth);
                setWidth(_w);
                setLocalStorage(WIDTH_SETTING_KEY, _w);
              }}
            >
          <div className="right-section" style={{ width }}>
              <PinnableFields />
          </div>
            </ACPanelResize>
        </div>
      </ACPageContainer>
    </>
  );
};

export default FindingDetailsModalV3;
